//Contracts:
import { globals } from 'config/globals';
import { particles } from 'app/contracts/Particles';
import { Vector2 } from 'app/contracts/Geometry';
//Enums:
import { HomeParticleStateType } from 'app/enum/Particles';
//Library:
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import { ParticleField } from 'particles/ParticleField';
import { ParticlesStage } from 'app/views/home/webgl/particles/scenes/ParticlesStage';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import type { DepartmentScene, HomeWebGLDeps } from 'app/views/home/webgl/webgl.types';

import DepartmentColours from 'app/views/home/webgl/particles/departments/DepartmentColours';
import ScreensDepartment from 'app/views/home/webgl/particles/departments/ScreensDepartment';
import IconDepartment from 'app/views/home/webgl/particles/departments/IconDepartment';
import SpacesDepartment from 'app/views/home/webgl/particles/departments/SpacesDepartment';
import IntroDepartment from 'app/views/home/webgl/particles/departments/IntroDepartment';
import TransitionParticles from 'app/views/home/webgl/particles/departments/TransitionParticles';

import DepartmentSwitcher from 'app/views/home/webgl/particles/departments/DepartmentSwitcher';

import { DepartmentType } from 'app/data/Enums';
import SystemsDepartment from 'app/views/home/webgl/particles/departments/SystemsDepartment';
import DelayHelper from 'utils/timers';
import { device } from 'utils/device';

export class HomeField extends ParticleField {
	//
	private _colourProcessor: DepartmentColours;
	private _sceneProgressProcessor: DepartmentSwitcher;
	private _transitionParticlesProcessor: TransitionParticles;

	//Scene processor. In the main only one will be active at once:
	private _screensProcessor: ScreensDepartment;
	private _spacesProcessor: SpacesDepartment;
	private _systemsProcessor: SystemsDepartment;
	private _iconProcessor: IconDepartment;
	private _introProcessor: IntroDepartment;

	//Only one scene is active at a time as need to share the particles (and reduce complexity)
	private _activeSceneProcessor: DepartmentScene;

	private _screensPixelData: any;
	private _iconPixelData: Array<Vector2>;

	//Host callbacks from ParticlesWebGL (px->rel conversion reads
	//getVirtualWidth; the colour processor gets the colourise* callbacks):
	private _webglDeps: HomeWebGLDeps;

	//The stage built by the subContainersFactory during super() — the typed
	//home for the ex-globals scene refs (iconContainer / systemsContainer /
	//spacesOutter / spacesMask / ttt). Readers reach it via the `stage` getter:
	private _stage: ParticlesStage;


	private _frozen: boolean = false;

	constructor(sceneProps: particles.SceneProps, container: PIXI.Container, config: particles.ParticleFieldConfig) {
		//ParticlesStage builds the entire home sub-container tree, and needs the
		//field's just-created _particleContainer (it sets its flags and mounts
		//the sub-tree on it in a load-bearing child order) — which only exists
		//partway through ParticleField's constructor. So rather than building
		//the stage here before super(), we hand ParticleField a factory that it
		//invokes at exactly the point the old inline home block ran (right
		//after the particle container is created and mounted):
		let stage: ParticlesStage = null;
		config.subContainersFactory = (particleContainer: PIXI.Container) => {
			//sceneProps.textures is set by ParticlesWebGL.create() before the
			//particle manager (and so this field) is constructed:
			stage = new ParticlesStage(particleContainer, sceneProps.textures);
			return stage.subContainers;
		};

		super(sceneProps, container, config);

		//The factory ran synchronously inside super(), so the stage exists now:
		this._stage = stage;

		//Was the first statement of ParticleField's constructor (gated on the
		//'home-particle-field' id check). Nothing reads sceneProps.homeField
		//synchronously during field construction (particle creation and init()
		//are deferred), so registering right after super() is equivalent
		//(ex globals.homeField — read by ScreensGridParticle).
		//Deliberately registered on the field, not the stage:
		this._sceneProps.homeField = this;

		//Per-index sub-container selection (driven by HomeFieldConfig's particle
		//pool index ranges) used to live in ParticleField._processParticle; it is
		//now injected as the particle factory. Particle creation is deferred
		//(setTimeout in ParticleField._processParticles), so assigning the
		//factory right after super() is always before the first particle is
		//constructed. Unmatched indexes fall back to the field's default
		//particle container, exactly as the old `null` target did:
		const particlePoolIndexes = HomeFieldConfig.indexes();
		config.particleFactory = (particleConfig: particles.ParticleConfig, index: number) => {
			let targetContainer: PIXI.Container = this._particleContainer;
			if (index <= particlePoolIndexes.transitions.upper) targetContainer = stage.getSubContainer('transitions');
			else if (index >= particlePoolIndexes.icon.lower && index <= particlePoolIndexes.icon.upper)
				targetContainer = stage.getSubContainer('icon');
			else if (index >= particlePoolIndexes.screens.lower && index <= particlePoolIndexes.screens.upper)
				targetContainer = stage.getSubContainer('screens');
			else if (index >= particlePoolIndexes.spaces.lower && index <= particlePoolIndexes.spaces.upper)
				targetContainer = stage.getSubContainer('spaces');
			else if (index >= particlePoolIndexes.systems.lower && index <= particlePoolIndexes.systems.upper)
				targetContainer = stage.getSubContainer('systems');

			return new HomeParticle(sceneProps, targetContainer, particleConfig);
		};
	}

	/** The home scene graph (typed ex-globals refs) — see ParticlesStage. */
	public get stage(): ParticlesStage {
		return this._stage;
	}

	public get mousedown(): boolean {
		return this._sceneProps.mouse.isDown;
	}

	public getRelativeWidth(widthPx: number): number {
		return widthPx / this._webglDeps.getVirtualWidth();
	}

	public getRelativeHeight(heightPx: number): number {
		return heightPx / device.innerHeight;
	}

	public resize(): void {
		super.resize();
	}

	public resizeEvent() {
		//Every scene inherits a resize() (no-op by default in
		//BaseDepartmentScene), so the resize is forwarded to whichever scene is
		//active — previously an identity-check chain over screens/systems/intro,
		//the only scenes with real resize bodies.
		this._activeSceneProcessor.resize();
	}

	public freeze() {
		this._frozen = true;
	}

	public unfreeze() {
		this._frozen = false;
	}

	protected init(): void {
		//Set all core particle data:
		this.particles.forEach((particle: HomeParticle, index: number) => {
			particle.index = index;
		});

		//Create the constant processors. These just handle things like colour switching the contant transition type particle pool:
		this._colourProcessor = new DepartmentColours(this, this.particles as Array<HomeParticle>);
		this._transitionParticlesProcessor = new TransitionParticles(this, this._sceneProps);

		//Create the scene processors. Only one will probably be active at a time.
		this._introProcessor = new IntroDepartment(this, this._sceneProps);

		this._iconProcessor = new IconDepartment(this, this._sceneProps);

		if (this._iconPixelData) {
			this._iconProcessor.setIconPixelData(this._iconPixelData);
		}

		this._screensProcessor = new ScreensDepartment(this, this._sceneProps);
		this._screensProcessor.pixelData = this._screensPixelData;

		this._spacesProcessor = new SpacesDepartment(this, this._sceneProps, this._colourProcessor);

		this._systemsProcessor = new SystemsDepartment(this, this._sceneProps, this._colourProcessor);

		//Under normal conditions the intro is the start processor
		this._activeSceneProcessor = this._introProcessor;

		//Set progress processor. Handles switching between main icon/screens/spaces scenes:
		this._sceneProgressProcessor = new DepartmentSwitcher(this, this._sceneProps, {
			spaces: this._spacesProcessor,
			screens: this._screensProcessor,
			systems: this._systemsProcessor,
			icon: this._iconProcessor,
			transitions: this._transitionParticlesProcessor
		});
	}

	public set webglDeps(v: HomeWebGLDeps) {
		this._webglDeps = v;
		this._colourProcessor.setWebglDeps(v);
	}

	public setPixelData(pixelData: any) {
		this._screensPixelData = pixelData;
	}

	public setIconGrid(pixelData: Array<Vector2>) {
		this._iconPixelData = pixelData;
		this._iconProcessor.setIconPixelData(this._iconPixelData);
	}

	/** Intro interactive state */
	public startIntroScene() {
		this._activeSceneProcessor = this._introProcessor;
		this._activeSceneProcessor.startAsActiveScene();
	}

	/** Mouse Over on main scene */
	public introMouseOver() {
		this._introProcessor.mouseOver();
	}

	/** Mouse out on main scene */
	public introMouseOut() {
		this._introProcessor.mouseOut();
	}

	public introComplete() {
		this._introProcessor.introComplete();
	}

	/** Intro interaction is clear so can start at icon processor */
	public startMainScene() {
		this._spacesProcessor.stopAsActiveScene();
		this._screensProcessor.stopAsActiveScene();
		this.setActiveSceneProcessor(this._iconProcessor);
		this._transitionParticlesProcessor.toIcon();
		this._sceneProgressProcessor.startMainScene('icon');
	}

	public changeIconColourOnHover() {
		if (this._colourProcessor) {
		}
	}

	/** Scene has been switched so  */
	public setActiveSceneProcessor(v: DepartmentScene) {
		if (this._activeSceneProcessor) {
			this._activeSceneProcessor.stopAsActiveScene();
		}

		this._activeSceneProcessor = v;

		globals.skipNormalParticleUpdates = true;
		DelayHelper.setTimeout(() => {
			globals.skipNormalParticleUpdates = true;
			this._activeSceneProcessor.startAsActiveScene();

		}, 10);
	}

	public setupForWork(department: DepartmentType): void {

		if (department === DepartmentType.SCREENS) {
			this._screensProcessor.setupForWork();
		} else {
			this._spacesProcessor.setupForWork();
		}
	}

	public showFromWork(department: DepartmentType) {

		if (department === DepartmentType.SCREENS) {
			this._screensProcessor.showFromWork();
		} else if (department === DepartmentType.SPACES) {
			this._spacesProcessor.showFromWork();
		} else {
			this._systemsProcessor.showFromWork();
		}
	}

	public animateToWork(department: DepartmentType): void {
		this._transitionParticlesProcessor.toWork(department);

	}

	public animateFromWork(department: DepartmentType) {

		if (department === DepartmentType.SCREENS) {
		} else {
		}
	}

	public updateSystemsGrid() {
		this._systemsProcessor.updateGrid();
	}

	public tick(): void {

		if (this._frozen || !this._sceneProgressProcessor) {
			return;
		}

		this._sceneProgressProcessor.tick();
		this._colourProcessor.tick();
		this._transitionParticlesProcessor.tick();

		if (this._activeSceneProcessor) {
			this._activeSceneProcessor.tick();
		}

		if (!this._systemsProcessor.isActive) {
			this._systemsProcessor.tick();
		}
	}

	//Stub — the state-change choreography that used to live here is gone; the
	//body has been empty for a while. skipFieldJump is accepted because
	//HomeParticleManager still forwards it (ParticlesWebGL passes true on the
	//HOME deep-link path); both arguments are ignored until/unless the
	//choreography returns:
	public changeState(state: HomeParticleStateType, skipFieldJump: boolean = false): void {}

	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number): void {
		if (this._transitionParticlesProcessor) {
			this._transitionParticlesProcessor.updateWorkTransitionParticles(introXPx, introProgressRatio);
		}
	}
}
